/** Insight — the same report for any date range (plan 2026-10-21-aso-auto-report).
 *
 *  The range is in the URL (?from=&to=) and defaults to the last 30 days
 *  ending yesterday, Seoul time. 24시간 and 3일 end today from noon on. Everything is
 *  compared with the range of the same length just before it, except that a
 *  range ending today is shown without changes (the day is not over), and App
 *  Store days not published yet are left out of both ranges. Days with no
 *  data are listed under the coverage warnings. */

import { useState } from 'react'
import { Download, Lightbulb } from 'lucide-react'
import type { InsightReport, StoreKey } from '@/api/client'
import { useInsight } from '@/lib/queries'
import { Bars, EmptyState, ErrorState, Field, Input, Notice, PageHeader, Pager, Segmented, Skeleton, buttonClass, useQueryParam, useSetQueryParams } from '@/ui'
import { RevenueColumns, columnsOfDays } from '../reports/charts'
import { addDays, fmtValue, kstToday, md, num, won } from '../reports/format'
import { AppTable, Change, CountryTable, Facts, Kpi, KPI_GRID, KPI_LAST, KeywordMoves, MetricTable, PaymentsTable, ReviewList, SalesNote, Section, ShareBars, StoreBar, StoreSplit } from '../reports/parts'

const PAGE = 20
const MAX_DAYS = 400

export type PresetKey = '1' | '3' | '7' | '30' | 'month' | 'last-month' | '90'
export const PRESETS: { value: PresetKey; label: string }[] = [
  { value: '1', label: '24시간' },
  { value: '3', label: '3일' },
  { value: '7', label: '최근 7일' },
  { value: '30', label: '최근 30일' },
  { value: 'month', label: '이번 달' },
  { value: 'last-month', label: '지난달' },
  { value: '90', label: '최근 90일' },
]

/** The range a preset means (Seoul dates). Data comes in whole days, so
 *  24시간 and 3일 are the days that overlap the last 24 or 72 hours the most:
 *  they end today from noon on and yesterday before it. The rest end
 *  yesterday unless it is a month. */
export function presetRange(key: PresetKey, now = new Date()): { from: string; to: string } {
  const today = kstToday(now)
  const yesterday = addDays(today, -1)
  const recent = new Date(now.getTime() + 9 * 3_600_000).getUTCHours() >= 12 ? today : yesterday
  switch (key) {
    case '1':
      return { from: recent, to: recent }
    case '3':
      return { from: addDays(recent, -2), to: recent }
    case '7':
      return { from: addDays(yesterday, -6), to: yesterday }
    case '90':
      return { from: addDays(yesterday, -89), to: yesterday }
    case 'month':
      return { from: yesterday.slice(0, 8) + '01', to: yesterday }
    case 'last-month': {
      const first = yesterday.slice(0, 8) + '01'
      const to = addDays(first, -1)
      return { from: to.slice(0, 8) + '01', to }
    }
    default:
      return { from: addDays(yesterday, -29), to: yesterday }
  }
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export default function Insight() {
  const def = presetRange('30')
  const [fromQ] = useQueryParam('from', def.from)
  const [toQ] = useQueryParam('to', def.to)
  const setParams = useSetQueryParams()
  const from = DATE_RE.test(fromQ) ? fromQ : def.from
  const to = DATE_RE.test(toQ) ? toQ : def.to
  const span = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1
  const problem = from > to ? '시작일이 끝나는 날보다 늦다' : span > MAX_DAYS ? `최대 ${MAX_DAYS}일까지 볼 수 있다` : ''
  const q = useInsight(problem ? '' : from, problem ? '' : to)
  const active = PRESETS.find((p) => {
    const r = presetRange(p.value)
    return r.from === from && r.to === to
  })?.value

  const options = [...PRESETS, { value: 'custom' as const, label: '직접 입력' }]
  return (
    <>
      <PageHeader
        title="Insight"
        description={`${from} ~ ${to} (${span}일)`}
        actions={
          problem ? undefined : (
            <a className={buttonClass('secondary')} href={`/api/insight/md?from=${from}&to=${to}`} download>
              <Download size={16} aria-hidden />
              md 내려받기
            </a>
          )
        }
      >
        <div className="flex flex-wrap items-end gap-3">
          <Segmented
            label="기간"
            options={options}
            value={active ?? 'custom'}
            onChange={(v) => {
              if (v === 'custom') return
              const r = presetRange(v as PresetKey)
              setParams({ from: r.from, to: r.to })
            }}
          />
          <Field label="시작">
            <Input type="date" value={from} max={to} onChange={(e) => e.target.value && setParams({ from: e.target.value, to })} />
          </Field>
          <Field label="끝" error={problem || undefined}>
            <Input type="date" value={to} min={from} onChange={(e) => e.target.value && setParams({ from, to: e.target.value })} />
          </Field>
        </div>
      </PageHeader>
      {problem ? null : q.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : q.error || !q.data ? (
        <ErrorState title="Insight를 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <Body key={`${from}|${to}`} r={q.data} />
      )}
    </>
  )
}

function metric(r: InsightReport, key: string) {
  return r.metrics.find((m) => m.key === key)
}

/** A tile's change; a range that ends today shows the range before as a
 *  figure instead, since today is not over. */
function Versus({ open, fmt, cur, prev, prevLabel }: { open: boolean; fmt: 'krw' | 'int'; cur?: number | null; prev?: number | null; prevLabel?: string }) {
  if (open) return <div>지금까지 · 이전 {fmtValue(fmt, prev)}</div>
  return (
    <div>
      <Change fmt={fmt} cur={cur} prev={prev} /> {prevLabel && <span>{prevLabel.startsWith('App') ? prevLabel : `vs ${prevLabel}`}</span>}
    </div>
  )
}

/** What the latest days still lack: today is not over, the App Store
 *  publishes a day the next evening, Analytics is two or three days behind. */
function Recent({ r }: { r: InsightReport }) {
  if (!r.open && !r.appstore_through && !r.funnel_through) return null
  return (
    <Notice tone="scheduled" title="최근 날짜는 아직 다 들어오지 않았다">
      <ul className="space-y-0.5">
        {r.open && <li>오늘은 지금까지 들어온 값이라 이전 구간과 비교하지 않는다(변화 칸 비움).</li>}
        {r.appstore_through && <li>App Store 판매는 {md(r.appstore_through)}까지 나왔다. 하루치가 다음 날 저녁에 나온다. 이전 구간도 같은 날 수만큼 App Store를 뺐다.</li>}
        {r.funnel_through && <li>노출·전환(App Store Analytics)은 {md(r.funnel_through)}까지 나왔다. Apple이 2~3일 늦게 준다.</li>}
      </ul>
    </Notice>
  )
}

function Body({ r }: { r: InsightReport }) {
  const [page, setPage] = useState(1)
  const days = r.day_points
  const gaps = r.coverage.filter((c) => !c.covers)
  const open = !!r.open
  // No App Store day in the range at all (24시간 before the evening after).
  const noAppstore = !!r.appstore_through && r.appstore_through < r.from
  const notIn: StoreKey[] | undefined = noAppstore ? ['appstore'] : undefined
  const rev = r.revenue
  const dl = metric(r, 'downloads')
  const pay = metric(r, 'payments')
  const cvr = metric(r, 'cvr')
  const prevLabel = `${md(r.prev_from)}–${md(r.prev_to)}`
  const pageRows = r.payments.slice((page - 1) * PAGE, page * PAGE)
  return (
    <div className="space-y-6">
      {gaps.length > 0 && (
        <Notice tone="deadline" title="이 기간을 다 덮지 못하는 데이터가 있다">
          <ul className="space-y-0.5">
            {gaps.map((c) => (
              <li key={c.source}>
                {c.label}: {c.first ? `${c.first} ~ ${c.last ?? ''}` : '데이터 없음'}
                {c.note ? ` · ${c.note}` : ''}
              </li>
            ))}
          </ul>
        </Notice>
      )}
      <Recent r={r} />
      <div className="space-y-2">
        <div className={KPI_GRID}>
          <Kpi label="매출" value={won(r.sales.cur)}>
            <Versus open={open} fmt="krw" cur={r.sales.cur} prev={r.sales.prev} prevLabel={prevLabel} />
            <StoreSplit revenue={r.sales_by_store} noun="매출" pending={notIn} className="flex-col" />
          </Kpi>
          <Kpi label="수익" value={won(rev.cur)}>
            <Versus open={open} fmt="krw" cur={rev.cur} prev={rev.prev} prevLabel={prevLabel} />
            <StoreSplit revenue={r.revenue_by_store} pending={notIn} className="flex-col" />
          </Kpi>
          <Kpi label="신규 다운로드" value={noAppstore ? '집계 전' : num(dl?.cur)}>
            <Versus open={open} fmt="int" cur={dl?.cur} prev={dl?.prev} prevLabel="App Store" />
          </Kpi>
          <Kpi label="결제 건수" value={noAppstore ? '집계 전' : num(pay?.cur)}>
            <Versus open={open} fmt="int" cur={pay?.cur} prev={pay?.prev} />
          </Kpi>
          <Kpi label="전환율" value={fmtValue('pct', cvr?.cur)} className={KPI_LAST}>
            <Change fmt="pct" cur={cvr?.cur} prev={cvr?.prev} />
          </Kpi>
        </div>
        <SalesNote />
      </div>
      <Facts items={r.facts} />
      <Section title="지표" hint={`이전 ${prevLabel}`}>
        <MetricTable rows={r.metrics} curLabel="이번" prevLabel="이전" />
      </Section>
      <Section title="일별 수익" hint="스토어별 누적">
        <RevenueColumns columns={columnsOfDays(days)} label="일별 수익, 스토어별" />
        <StoreBar revenue={r.revenue_by_store} />
      </Section>
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="신규 다운로드">
          <Bars labels={days.map((d) => d.date)} outer={days.map((d) => d.downloads)} tick={md} title={(i) => `${days[i].date} 신규 다운로드 ${num(days[i].downloads)}`} label="일별 신규 다운로드" />
        </Section>
        <Section title="노출">
          <Bars labels={days.map((d) => d.date)} outer={days.map((d) => d.impressions ?? 0)} outerFill="bg-chart-2" tick={md} title={(i) => `${days[i].date} 노출 ${num(days[i].impressions)}`} label="일별 노출" />
        </Section>
      </div>
      <Section title="앱별">
        <AppTable rows={r.apps} limit={15} cmp={!open} />
      </Section>
      <Section title="국가별">
        <CountryTable rows={r.countries} limit={15} />
      </Section>
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="종류별 비중">
          <ShareBars items={r.by_kind} label="종류별 비중" fill="bg-chart-2" />
        </Section>
        <Section title="스토어별 비중">
          <ShareBars items={r.by_store} label="스토어별 비중" fill="bg-chart-3" />
        </Section>
      </div>
      <Section title="결제 내역" hint={r.payments.length >= 300 ? '최신 300건까지' : undefined}>
        <PaymentsTable rows={pageRows} />
        <Pager total={r.payments.length} page={page} pageSize={PAGE} onPage={setPage} />
      </Section>
      <Section title="새 리뷰">
        <ReviewList rows={r.reviews} />
      </Section>
      <Section title="키워드 이동">
        <div className="grid gap-4 lg:grid-cols-2">
          <KeywordMoves rows={r.keywords_up} dir="up" />
          <KeywordMoves rows={r.keywords_down} dir="down" />
        </div>
      </Section>
      {r.metrics.length === 0 && <EmptyState icon={Lightbulb} title="이 기간에는 데이터가 없다" />}
    </div>
  )
}
